import { VFC } from 'react'
import { PremiumContribution } from 'src/models/PremiumContribution/index'
import { LoginUser } from 'src/models/User'

import { useEffect, useState } from 'react'
import { useMasterData } from 'src/api/masterData'
import { SearchIconLarge } from 'src/assets/svgs'
import { Pagination } from 'src/components/Pagination'
import { Link } from 'src/elements/Link'
import { fontWeightBold, fontWeightRegular } from 'src/styles/font'
import { CONTACT } from 'src/utils/routes/serverPaths'
import styled from 'styled-components'
import { ContentTagSelect } from './components/ContentTagSelect'
import { PremiumContributionCard } from './PremiumContributionCard'

type PremiumContributionLayoutProps = {
  user: LoginUser
  premiumContributions: PremiumContribution[]
  currentPage: number
  totalCount: number
  onePageDisplayItems: number
  keyword: string
  contentTag?: string
  status?: string
  entryStatus?: string
  showProfile: (id: number) => void
  setPage: (page: number) => void
  onSearch: (searchParams: {
    keyword?: string
    contentTag?: string
    status?: string
    entryStatus?: string
  }) => void
}

export const PremiumContributionLayout: VFC<PremiumContributionLayoutProps> = ({
  user,
  premiumContributions,
  currentPage,
  totalCount,
  onePageDisplayItems,
  contentTag: initialContentTag,
  status: initialStatus,
  entryStatus: initialEntryStatus,
  keyword,
  showProfile,
  setPage,
  onSearch,
}) => {
  // マスターデータを取得
  const { data: masterData } = useMasterData()

  // 検索フォーム用state
  const [contentTag, setContentTag] = useState(initialContentTag || '')
  const [status, setStatus] = useState(initialStatus || '')
  const [entryStatus, setEntryStatus] = useState(initialEntryStatus || '')
  const [searchKeyword, setSearchKeyword] = useState(keyword || '')

  // 親コンポーネントから渡される検索条件とフォームのstateを同期
  useEffect(() => {
    setContentTag(initialContentTag || '')
    setStatus(initialStatus || '')
    setEntryStatus(initialEntryStatus || '')
    setSearchKeyword(keyword || '')
  }, [initialContentTag, initialStatus, initialEntryStatus, keyword])

  // 検索ボタン押下時の処理
  const handleSearch = () => {
    onSearch({
      keyword: searchKeyword.trim(),
      contentTag,
      status,
      entryStatus,
    })
  }

  return (
    <Content>
      <SearchFormWrapper>
        <SearchTitle>案件を探す</SearchTitle>
        <SearchDescriptionWrapper>
          {user.plan.paid || user.plan.isAdminRole ? (
            <SearchDescription>
              掲載されている案件を検索・閲覧できるのはもちろん、案件の詳細を確認した上でエントリーすることが可能です。
              <br />
              検索条件（案件タグ、ステータス、エントリー状況、キーワード）を指定して案件を絞り込み、
              <br />
              あなたに合った案件を効率的に見つけられます。
            </SearchDescription>
          ) : (
            <>
              <SearchDescription>
                掲載されている案件を検索・閲覧することができます。
                <br />
                検索条件（案件タグ、ステータス、エントリー状況、キーワード）を指定して案件を絞り込み、
                <br />
                あなたに合った案件を効率的に見つけられます。
                <br />
                ※案件のエントリーには有料会員登録が必要です。
                <br />
                ※有料会員への登録には1～2営業日お時間をいただきます。
              </SearchDescription>
              <SearchPaidPlanButton href={CONTACT}>
                有料プランについて話を聞く
              </SearchPaidPlanButton>
            </>
          )}
        </SearchDescriptionWrapper>
        <SearchTitle>案件を検索する</SearchTitle>
        <SearchForm>
          <FormRow>
            <FormItem>
              <FormLabel>案件タグ</FormLabel>
              <ContentTagSelect
                value={contentTag}
                onChange={setContentTag}
                items={masterData?.contentTags || []}
                placeholder="選択してください"
              />
            </FormItem>
            <FormItem>
              <FormLabel>ステータス</FormLabel>
              <FormSelect
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setStatus(e.target.value)
                }
              >
                <option value="">選択してください</option>
                <option value="publish">募集中</option>
                <option value="close">募集終了</option>
              </FormSelect>
            </FormItem>
            <FormItem>
              <FormLabel>エントリー状況</FormLabel>
              <FormSelect
                value={entryStatus}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setEntryStatus(e.target.value)
                }
              >
                <option value="">選択してください</option>
                <option value="applied">応募済み</option>
                <option value="not_applied">未応募</option>
              </FormSelect>
            </FormItem>
          </FormRow>
          <FormRow>
            <FormItem>
              <FormLabel>キーワード</FormLabel>
              <FormInput
                type="text"
                value={searchKeyword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchKeyword(e.target.value)
                }
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') handleSearch()
                }}
                placeholder="キーワードを入力"
              />
            </FormItem>
            <SearchButton onClick={handleSearch}>
              <SearchIconLarge />
              検索する
            </SearchButton>
          </FormRow>
        </SearchForm>
      </SearchFormWrapper>

      {totalCount > 0 ? (
        <SearchCount>
          {totalCount}件中{onePageDisplayItems * (currentPage - 1) + 1}～
          {Math.min(onePageDisplayItems * currentPage, totalCount)}件表示中
        </SearchCount>
      ) : (
        <SearchCount>0件中0件表示中</SearchCount>
      )}

      {premiumContributions.length > 0 ? (
        <>
          <ContributionCards>
            {premiumContributions.map((contribution) => (
              <PremiumContributionCard
                key={contribution.id}
                contribution={contribution}
                showProfile={showProfile}
              />
            ))}
          </ContributionCards>
          <Pagination
            itemSize={totalCount}
            setIndex={setPage}
            forcePage={currentPage - 1}
            onePageDisplayItems={onePageDisplayItems}
          />
        </>
      ) : (
        <Empty>案件が見つかりませんでした</Empty>
      )}
    </Content>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 48px 20px;
  width: 100%;
  max-width: 1084px;
  margin-left: auto;
  margin-right: auto;
  @media screen and (max-width: 768px) {
    padding: 24px 20px;
  }
`

// 検索フォーム用スタイル
const SearchFormWrapper = styled.div`
  border-top-right-radius: 64px;
  border-bottom-left-radius: 64px;
  padding: 40px 40px 56px 40px;
  margin-bottom: 48px;
  background: #f8f8f8;
  @media screen and (max-width: 768px) {
    padding: 20px;
    border-radius: 0;
    margin-bottom: 24px;
  }
`

const SearchTitle = styled.h1`
  font-size: 16px;
  font-weight: ${fontWeightBold};
  margin-bottom: 24px;
  @media screen and (max-width: 768px) {
    margin-bottom: 16px;
  }
`

const SearchDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 40px;
  @media screen and (max-width: 768px) {
    margin-bottom: 24px;
    gap: 16px;
  }
`

const SearchDescription = styled.div`
  font-size: 14px;
  color: #393e4c;
  line-height: 1.5;
`

const SearchPaidPlanButton = styled(Link)`
  max-width: 400px;
  width: 100%;
  padding: 24px 30px;
  font-size: 22px;
  font-weight: ${fontWeightRegular};
  color: #ff6c01;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #ff6c01;
  border-radius: 4px;
  margin-left: auto;
  margin-right: auto;
  @media screen and (max-width: 768px) {
    padding: 16px 24px;
    font-size: 16px;
  }
`

const SearchCount = styled.div`
  font-size: 14px;
  font-weight: ${fontWeightRegular};
  color: #393e4c;
  margin-bottom: 24px;
  @media screen and (max-width: 768px) {
    margin-bottom: 16px;
  }
`
const SearchForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media screen and (max-width: 768px) {
    gap: 16px;
  }
`

const ContributionCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  row-gap: 32px;
  column-gap: 24px;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 16px;
    column-gap: 0;
  }
`

const Empty = styled.div`
  border-top-right-radius: 64px;
  border-bottom-left-radius: 64px;
  padding: 100px 40px;
  background: #f8f8f8;
  text-align: center;
  font-size: 14px;
  @media screen and (max-width: 768px) {
    padding: 24px;
    border-radius: 0;
    margin-bottom: 24px;
  }
`

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 24px;
  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const FormItem = styled.div`
  display: block;
  width: 240px;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`
const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`
const FormSelect = styled('select')`
  width: 100%;
  display: block;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px 36px 15px 20px;
  font-size: 14px;
  background: #fafafa;
  background: white;
`
const FormInput = styled('input')`
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px 36px;
  font-size: 14px;
  background: #fafafa;
  border-radius: 8px;
  padding: 15px 20px 15px 20px;
  background: white;
`
const SearchButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ff8800;
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  padding: 15px 20px 15px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #ff9900;
  }
  svg {
    width: 20px;
    height: 20px;
    margin-right: 4px;
    path {
      stroke: white;
    }
  }
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`
